import { Checkbox, FormControlLabel, LinearProgress } from '@mui/material'
import AvatarComponent from 'components/avatar'
import BackComponent from 'components/back'
import ButtonMobileComponent from 'components/button-mobile'
import { AssignmentIndIcon } from 'components/icons'
import CarpoolDashboardLink from 'components/link-carpool-dashboard'
import WizardComponent from 'components/wizard'
import Arrow from 'images/arrow_forward.png'
import React, { useEffect, useMemo, useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { RequiredFields } from 'types/CommonTypes'
import { RoomType, RoomUsersOutputProps, UserRoomsOutputProps } from 'types/RoomTypes'
import { UsersOutputProps } from 'types/UserTypes'
import { isWeb, METERS_TO_MILES, titleCaseSchoolNameForUI } from 'utils/constants'
import { getCommunityUsersSortedByDistance } from 'utils/userUtils'

// Mock Data Types
interface RoomAddUsersComponentProps {
  users: UsersOutputProps[] // all users
  room: UserRoomsOutputProps // room to add users
  roomUsers: RoomUsersOutputProps[] // all room users
  userId: string // current user creating/adding
  failedPhotoLinks: Set<string>
  onFailedPhotoLink: (url: string) => void
  onAddRoomUsers: (id: string, users: string[]) => Promise<void>
}

interface CardComponentProps {
  isChecked: boolean
  avatar?: JSX.Element
  title: string
  subtitle?: string
  body?: string
  onChange: (isChecked: boolean) => void
}

const CardComponent = (props: CardComponentProps): JSX.Element => {
  const { isChecked, avatar, title, subtitle, body, onChange } = props ?? {}

  return (
    <div
      className={`
        flex flex-col my-2 p-3 rounded-lg
        transition-colors duration-150
        ${isChecked ? 'bg-[#FFFDF5]' : 'hover:bg-gray-50'}
      `}
    >
      <FormControlLabel
        className='m-0 w-full'
        componentsProps={{
          typography: {
            component: 'div',
            className: 'w-full',
          },
        }}
        control={
          <Checkbox
            data-testid='checkbox-testid'
            value={isChecked}
            onChange={(e) => onChange(e.target.checked)}
            sx={{
              strokeWidth: 1,
              '&.Mui-checked': {
                color: '#EBD76F',
              },
              '&:hover': {
                backgroundColor: 'rgba(235, 215, 111, 0.08)',
              },
            }}
          />
        }
        label={
          <div className='flex gap-3 items-start w-full'>
            {avatar && <div className='mt-0.5'>{avatar}</div>}
            <div className='flex flex-col gap-0.5'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <span className='text-[#1A1A1A] text-sm font-medium leading-tight'>{title}</span>
                {subtitle && (
                  <>
                    <span className='w-px bg-gray-200 h-4' />
                    <span className='text-[#666666] text-xs font-normal'>{subtitle}</span>
                  </>
                )}
              </div>
              <span
                className={`${body ? 'text-[#FF7900] text-sm' : 'text-[#666666] text-xs'} font-normal leading-tight mt-0.5`}
              >
                {body || 'Location not set'}
              </span>
            </div>
          </div>
        }
      />
    </div>
  )
}

const RoomAddUsersComponent: React.FC<RoomAddUsersComponentProps> = ({
  users,
  room,
  roomUsers,
  userId,
  failedPhotoLinks,
  onFailedPhotoLink,
  onAddRoomUsers,
}) => {
  // 1. State Hooks
  const navigate: NavigateFunction = useNavigate()
  const [filteredUsers, setFilteredUsers] = useState<CardComponentProps[]>([])
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const excludedUserIds = useMemo(() => new Set(roomUsers.map((v) => v.userId)), [roomUsers])

  // 2. Derived Constants
  const { roomType, roomId, roomCommunityId } = room as RequiredFields<UserRoomsOutputProps, 'roomCommunityId'>

  // 3. Effect Hooks
  // Filter, dedupe, and sort users by distance
  useEffect(() => {
    // Filter out duplicate users and sort by distance & username, filtering out my user id
    const sortedUsers = getCommunityUsersSortedByDistance(users, userId, roomCommunityId)
    const cards: CardComponentProps[] = sortedUsers
      .filter((v) => !excludedUserIds.has(v.userId))
      .map((user: UsersOutputProps & { _distance: number }) => {
        const { userId, userName, userPhotoLink, _distance } = user
        return {
          isChecked: false,
          avatar: (
            <AvatarComponent
              name={userName}
              photoLink={userPhotoLink}
              failedPhotoLinks={failedPhotoLinks}
              onFailedPhotoLink={onFailedPhotoLink}
            />
          ),
          title: userName,
          body: _distance !== Infinity ? `${Math.round(_distance * METERS_TO_MILES * 10) / 10} miles away` : '',
          onChange: (isChecked: boolean) =>
            isChecked
              ? setSelectedUsers((prev) => new Set(prev).add(userId))
              : setSelectedUsers((prev) => {
                  const curr = new Set(prev)
                  curr.delete(userId)
                  return curr
                }),
        }
      })
    setFilteredUsers(cards)
  }, [userId, excludedUserIds, users, roomCommunityId, failedPhotoLinks, onFailedPhotoLink])

  // 4. Derived State, Validations and Early Returns

  // 5. Event Handlers
  if (roomType !== RoomType.GROUP) return <LinearProgress />

  // 6. Render
  // body
  const body: JSX.Element = (
    <div className={`flex flex-col flex-1 self-stretch gap-2 ${isWeb ? '' : 'px-5'}`}>
      {/* **************** Show School **************** */}
      <div className='mb-2'>
        <div className='text-sm font-medium text-gray-600 mb-1'>Your School</div>
        <div className='flex items-center h-12 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-800'>
          {titleCaseSchoolNameForUI(roomCommunityId)}
        </div>
      </div>
      {/* **************** User List **************** */}
      {filteredUsers.length > 0 ? (
        <>
          <CarpoolDashboardLink
            userId={userId}
            communityId={room.roomCommunityId || ''}
            users={users}
            message='Set up location & view family profiles'
            className='justify-end'
          />
          {filteredUsers.map((u: CardComponentProps, index: number) => (
            <CardComponent key={index} {...u} />
          ))}
        </>
      ) : (
        <div className='flex flex-col items-center justify-center p-8 text-center'>
          <AssignmentIndIcon size={64} color='gray' />
          <h2 className='text-2xl font-semibold mb-2'>No Users Available</h2>
          <p className='text-gray-600 mb-6'>Check back later as more families join.</p>
        </div>
      )}
    </div>
  )

  // Filtered Rooms
  return (
    <div className='flex flex-col min-h-screen'>
      {/***************** Mobile *****************/}
      {!isWeb ? (
        <div className='flex flex-col flex-1'>
          <div className='sticky top-0 z-10 bg-white'>
            <BackComponent text='Add Members' />
          </div>
          <div className='flex-1 pb-24'>{body}</div>
          <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pt-3 pb-7 px-5'>
            <ButtonMobileComponent
              text='Submit'
              color='bg-custom-yellow'
              data_testid='save-button-testid'
              width='w-full'
              disabled={!selectedUsers?.size}
              onClick={() => onAddRoomUsers(roomId, [...Array.from(selectedUsers)])}
              iconPosition='right'
              icon={Arrow}
            />
          </div>
        </div>
      ) : (
        <WizardComponent
          btnText='Save'
          onCancel={() => navigate(-1)}
          onClick={() => onAddRoomUsers(roomId, [...Array.from(selectedUsers)])}
          title='Add Members'
          data_testId='save-button-testid'
          disable={!selectedUsers?.size}
        >
          {body}
        </WizardComponent>
      )}
    </div>
  )
}

export default RoomAddUsersComponent
